import type { GTMConfig, GTMEventParams } from './types';
import { isBrowser, logDebug } from '../core/utils';
import { DEFAULT_GTM_CONTAINER_ID } from './const';
import { getGtagKey, pushToDataLayer } from './helpers';

/**
 * Google Tag Manager
 */
class GoogleTagManager {
  private containerId: string = DEFAULT_GTM_CONTAINER_ID;
  private initialized: boolean = false;
  private debug: boolean = false;

  /**
   * 初始化 GTM
   * @param config GTM 配置
   */
  init(config?: GTMConfig): void {
    if (!isBrowser()) {
      logDebug('GTM initialization skipped: not in browser environment');
      return;
    }

    if (this.initialized) {
      logDebug('GTM already initialized');
      return;
    }

    if (config?.containerId) {
      this.containerId = config.containerId;
    }

    if (config?.debug) {
      this.debug = config.debug;
    }

    this.injectScript();
    this.injectNoScript();
    this.initialized = true;

    logDebug('GTM initialized');
  }

  /**
   * 注入 GTM 脚本
   */
  private injectScript(): void {
    const script = document.createElement('script');
    script.textContent = `
      (function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', '${this.containerId}')
    `;
    document.head.appendChild(script);
    script.remove();
  }

  /**
   * 注入 NoScript 标签
   */
  private injectNoScript(): void {
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.containerId}`;
    iframe.style.display = 'none';
    iframe.style.height = '0';
    iframe.style.width = '0';
    noscript.appendChild(iframe);
    document.body.appendChild(noscript);
  }

  /**
   * 推送自定义事件
   * @param eventName 事件名称
   * @param params 事件参数
   */
  pushEvent(eventName: string, params?: GTMEventParams): void {
    if (!this.initialized) {
      logDebug('GTM not initialized, skipping event', { eventName, params });
      return;
    }

    const eventData = {
      event: eventName,
      ...params,
    };

    pushToDataLayer(eventData);

    if (this.debug) {
      logDebug('Event pushed', eventData);
    }
  }

  /**
   * 触发用户注册事件
   */
  signUpEvent(): void {
    this.pushEvent('sign_up');
  }

  /**
   * 触发网站生成事件
   */
  generateEvent(): void {
    this.pushEvent('generate');
  }

  /**
   * 触发网站发布事件
   */
  publishEvent(): void {
    this.pushEvent('publish');
  }

  /**
   * 触发订阅购买事件
   * @param subscribeId 订阅 ID
   * @param planKey 套餐 key (可选,如果不传则需要从外部获取)
   */
  subscribeEvent(subscribeId: string, planKey?: string): void {
    const gtagConfig = planKey ? getGtagKey(planKey as any) : null;

    if (!gtagConfig) {
      logDebug('Invalid plan key or no plan key provided', { planKey });
      return;
    }

    // 设置 Analytics items
    if (isBrowser()) {
      window._analytics_items = [
        {
          item_id: gtagConfig.analysis,
          item_name: gtagConfig.analysis,
          price: gtagConfig.value,
          quantity: 1,
        },
      ];
    }

    this.pushEvent('purchase', {
      plan: gtagConfig.analysis,
      transaction_id: subscribeId,
      value: gtagConfig.value as unknown as string,
    });
  }

  /**
   * 获取初始化状态
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 获取 Container ID
   */
  getContainerId(): string {
    return this.containerId;
  }
}

export default GoogleTagManager;
